import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import {
  PiCalendarBlank,
  PiCircle,
  PiSortAscending,
  PiSortDescending,
  PiTextAa,
} from 'react-icons/pi';
import { SortButton } from '../../components/SortButton';
import {
  ReplayVideoSortType,
  SortConfig,
  SortDirection,
} from './useReplayVideoFilters';

interface ReplayVideoSortProps {
  sortConfig: SortConfig;
  setSortConfig: React.Dispatch<React.SetStateAction<SortConfig>>;
}

const getSortLabel = (type: ReplayVideoSortType) => {
  switch (type) {
    case 'date':
      return 'Date';
    case 'name':
      return 'Name';
    case 'status':
      return 'Status';
    default:
      return 'Sort';
  }
};

export const ReplayVideoSort = ({
  sortConfig,
  setSortConfig,
}: ReplayVideoSortProps) => {
  const handleSortChange = (
    type: ReplayVideoSortType,
    direction?: SortDirection
  ) => {
    setSortConfig({
      type,
      direction:
        direction ||
        (type === sortConfig.type && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc'),
    });
  };

  const toggleDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SortButton
          direction={sortConfig.direction}
          label={getSortLabel(sortConfig.type)}
          hideLabel={true}
          asChild={true}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
          Sort by
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => handleSortChange('date')}
          className={sortConfig.type === 'date' ? 'bg-accent' : ''}
        >
          <PiCalendarBlank className="mr-2 size-4" />
          Date
          {sortConfig.type === 'date' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('name')}
          className={sortConfig.type === 'name' ? 'bg-accent' : ''}
        >
          <PiTextAa className="mr-2 size-4" />
          Name
          {sortConfig.type === 'name' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('status')}
          className={sortConfig.type === 'status' ? 'bg-accent' : ''}
        >
          <PiCircle className="mr-2 size-4" />
          Status
          {sortConfig.type === 'status' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleDirection}>
          <div className="flex items-center">
            {sortConfig.direction === 'asc' ? (
              <>
                <PiSortDescending className="mr-2 size-4" />
                Switch to descending
              </>
            ) : (
              <>
                <PiSortAscending className="mr-2 size-4" />
                Switch to ascending
              </>
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
